/**
 * @module app.shared
 */

import { encodeQueryString } from 'app/shared/lib/utilities';
import { ApiSetting, IApiModelSettings, IApiSettings, IApiPropertySettings, IApiRelationshipSettings } from 'app/shared/lib/api/api-types';

export function isApiSettings(setting: ApiSetting | undefined): setting is IApiSettings {
    return !!(setting && !Array.isArray(setting) && typeof setting === 'object')
}

export function isApiModelSettings(setting: ApiSetting | undefined): setting is IApiModelSettings {
    return isApiSettings(setting) && (typeof setting.$model === 'string');
}

export function isApiRelationshipSettings(setting: ApiSetting | undefined): setting is IApiRelationshipSettings {
    return isApiSettings(setting) && (setting.$rel === 'lookup' || setting.$rel === 'details');
}

export function isApiPropertySettings(setting: ApiSetting | undefined): setting is IApiPropertySettings {
    // property settings values are all optional so we can assume any setting is a property setting
    return isApiSettings(setting);
}

export function getApiSetting(settings: IApiSettings, path: string): ApiSetting | undefined {
    const parts = path.split('.');
    let current: ApiSetting | undefined = settings;
    for(const part of parts) {
        if(!isApiSettings(current)) { return undefined; }
        current = current[part];
    }
    return current;
}

interface ISettingsInternals {
    tenant: string | null;
    url: string | null;
    includes: Set<string>;
}

/**
 * API Settings Builder
 */
export class ApiSettings {

    private readonly internals: ISettingsInternals = {
        tenant: null,
        url: null,
        includes: new Set<string>(),
    };

    /**
     * Construct a new API Settings object
     *
     * **Note**: `api.settings()` is a shortcut to constructing this object
     * @param url the API URL for which to load settings
     */
    constructor(url: string) {
        this.internals.url = url;
    }

    /** Returns `true` if this settings request has a tenant code value specified */
    public hasTenant(): boolean {
        return !!this.internals.tenant;
    }

    /**
     * Specify the tenant code to use for this settings request
     * @param tenant the tenant code to settings request, or `null` to use the currently logged in tenant
     */
    public withTenant(tenant: string | null): this {
        this.internals.tenant = tenant;
        return this;
    }

    /**
     * Override the URL used to make this settings request
     * @param url the new URL to settings request against
     */
    public overrideUrl(url: string): this {
        this.internals.url = url;
        return this;
    }

    /**
     * Include the list of specified model references
     * @param references the model references to include
     */
    public include(...references: string[]): this {
        for(const reference of references) {
            this.internals.includes.add(reference.toLowerCase());
        }
        return this;
    }

    /**
     * Convert the current settings request to an API URL
     */
    public toString(): string {
        if(!this.internals.url) {
            return '';
        }

        const slash = this.internals.url.endsWith('/') ? '' : '/';
        const url = this.internals.tenant ? `${encodeURI(this.internals.tenant)}/${this.internals.url}${slash}!settings` : this.internals.url;
        const query: string[] = [];

        if(this.internals.includes.size > 0) {
            const includes: string[] = [];
            for(const include of this.internals.includes) {
                includes.push(encodeQueryString(include));
            }
            query.push(`include=${includes.join('+')}`);
        }

        return (query.length > 0 ? (`${url}?${query.join('&')}`) : url);
    }

}
